import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import clsx from "clsx";
import { Button } from "./Button";
import { useT } from "../i18n";

/**
 * Side panel on desktop (sticky column) that becomes a bottom sheet on phones (§6 J1). The sheet traps focus
 * like a dialog and closes on Escape; desktop keeps the page scrollable next to it.
 */
export function SidePanel({ open, onClose, title, children, footer, className }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode; className?: string }) {
  const t = useT();
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const l = () => setPhone(mq.matches);
    mq.addEventListener("change", l);
    return () => mq.removeEventListener("change", l);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    if (phone) { document.body.style.overflow = "hidden"; ref.current?.querySelector<HTMLElement>("textarea, input, button")?.focus(); }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [open, phone, onClose]);
  if (!open) return null;
  const body = (
    <div ref={ref} className={clsx("rounded-card border border-line bg-surface flex max-h-full min-h-0 flex-col", phone ? "rounded-b-none rounded-t-dialog border-0 shadow-e3" : "sticky top-20", className)} role={phone ? "dialog" : "complementary"} aria-modal={phone || undefined} aria-labelledby={id}>
      <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id={id} className="text-h2">{title}</h2>
        <Button variant="ghost" size="touch" className="-mr-2 px-2" onClick={onClose} aria-label={t("ui.close")}><X className="h-4 w-4" aria-hidden="true" /></Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
      {footer && <div className="flex flex-col gap-2 border-t border-line p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">{footer}</div>}
    </div>
  );
  if (phone) {
    return (
      <div className="fixed inset-0 z-40 flex items-end bg-[rgb(20_28_38/.45)]" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="max-h-[90vh] w-full">{body}</div>
      </div>
    );
  }
  return body;
}
